/* =========================================================================
   components.tsx — UI primitives: Icons, KPICard, Sidebar, Header,
                    Section, Flag, Field, NumberInput, etc.
   ========================================================================= */

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCountUp } from "./use-count-up";
import { useInView } from "./use-in-view";
import { useFocusTrap } from "./hooks";
import { parseNumericValue, formatParsed } from "./parse-numeric-value";
import type {
  KPICardProps,
  SectionProps,
  FlagItemProps,
  ModelResult,
  Scenario,
} from "./types";
import { useScenario } from "./state";
import { Model } from "./model";
import { filterScenariosBySite, groupScenariosBySite } from "./sites";
import { captureSvgSelector } from "./chart-export";
import { toastGlobal } from "./toast";
import { useAuth } from "./auth-context";
import { SyncStatus, deriveSyncState } from "./ws-sync/sync-status";
import { syncQueue } from "./persistence";
import { ImportExportUI } from "./ws-data/import-export-ui";
import { getOperatorDealRecommendation } from "./decision-metrics";
import { Wordmark } from "./wordmark";

// ---------------- Icons (lucide-ish, hand-rolled to avoid extra fetch) ----------------
interface IconProps {
  d: string | React.ReactNode;
  size?: number;
  stroke?: number;
}

interface IconFnProps {
  size?: number;
  stroke?: number;
  style?: React.CSSProperties;
}

const Icon: React.FC<Readonly<IconProps>> = ({ d, size = 16, stroke = 2 }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {typeof d === "string" ? <path d={d} /> : d}
  </svg>
);

export const Icons = {
  Summary: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 3v18h18" />
          <path d="M7 14l3-3 3 3 5-5" />
        </>
      }
    />
  ),
  Inputs: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M4 6h16" />
          <path d="M4 12h10" />
          <path d="M4 18h7" />
          <circle cx="18" cy="12" r="2" />
        </>
      }
    />
  ),
  Buildings: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M13 9h6v12" />
          <path d="M9 9h.01M9 13h.01M9 17h.01" />
        </>
      }
    />
  ),
  Operator: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 3v18h18" />
          <path d="M7 17V9" />
          <path d="M11 17v-5" />
          <path d="M15 17v-9" />
          <path d="M19 17V6" />
        </>
      }
    />
  ),
  Developer: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 6v6l4 2" />
        </>
      }
    />
  ),
  Sens: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 12h6" />
          <path d="M11 6h10" />
          <path d="M11 18h7" />
          <circle cx="9" cy="12" r="2" />
          <circle cx="21" cy="6" r="1" />
          <circle cx="18" cy="18" r="1" />
        </>
      }
    />
  ),
  Bench: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M5 4v16" />
          <path d="M19 4v16" />
          <path d="M5 8h6" />
          <path d="M13 12h6" />
          <path d="M5 16h10" />
        </>
      }
    />
  ),
  Check: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </>
      }
    />
  ),
  Report: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M8 13h8M8 17h5" />
        </>
      }
    />
  ),
  Reset: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
        </>
      }
    />
  ),
  Undo: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 7v6h6" />
          <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
        </>
      }
    />
  ),
  Redo: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M21 7v6h-6" />
          <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
        </>
      }
    />
  ),
  Plus: (p: IconFnProps) => <Icon {...p} d="M12 5v14M5 12h14" />,
  Trash: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </>
      }
    />
  ),
  Down: (p: IconFnProps) => <Icon {...p} d="M6 9l6 6 6-6" />,
  Up: (p: IconFnProps) => <Icon {...p} d="M6 15l6-6 6 6" />,
  Sliders: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M4 21v-7" />
          <path d="M4 10V3" />
          <path d="M12 21v-9" />
          <path d="M12 8V3" />
          <path d="M20 21v-5" />
          <path d="M20 12V3" />
          <path d="M1 14h6" />
          <path d="M9 8h6" />
          <path d="M17 16h6" />
        </>
      }
    />
  ),
  Quotes: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
          <path d="M8 13h8M8 17h5" />
        </>
      }
    />
  ),
  Star: (p: IconFnProps) => (
    <Icon
      {...p}
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77 5.82 21l1.18-6.88L2 9.27l6.91-1.01L12 2z"
    />
  ),
  Globe: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
        </>
      }
    />
  ),
  Pin: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </>
      }
    />
  ),
  Copy: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </>
      }
    />
  ),
  Edit: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
          <path d="m15 5 4 4" />
        </>
      }
    />
  ),
  Download: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </>
      }
    />
  ),
  Upload: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </>
      }
    />
  ),
  Print: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <polyline points="6 9 6 2 18 2 18 9" />
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
          <rect x="6" y="14" width="12" height="8" />
        </>
      }
    />
  ),
  Logout: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </>
      }
    />
  ),
  Brand: (p: IconFnProps) => (
    <svg
      viewBox="0 0 40 40"
      width={p.size || 28}
      height={p.size || 28}
      fill="none"
      style={p.style}
    >
      <rect
        x="7"
        y="22"
        width="6"
        height="12"
        rx="1"
        fill="currentColor"
        opacity="0.55"
      />
      <rect
        x="17"
        y="14"
        width="6"
        height="20"
        rx="1"
        fill="currentColor"
        opacity="0.78"
      />
      <rect x="27" y="6" width="6" height="28" rx="1" fill="currentColor" />
      <path
        d="M5 19 L20 7 L34 16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      />
    </svg>
  ),
  Eye: (p: IconFnProps) => (
    <Icon
      {...p}
      d={
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      }
    />
  ),
};

// ---------------- Print preview toggle ----------------

/**
 * Floating toggle for the on-screen "print layout" preview mode.
 *
 * Adds/removes the `print-preview` class on <body>, which mirrors the
 * `@media print` stylesheet on-screen: hides interactive chrome (sidebar,
 * header, tweaks), swaps to the print colour palette, and constrains the
 * shell to A4 proportions with a page shadow. The toggle itself stays
 * visible in preview mode (so the user can exit) but is hidden in real
 * print via `.no-print`.
 */
const PRINT_PREVIEW_CLASS = "print-preview";

export function PrintPreviewToggle() {
  const [active, setActive] = React.useState(false);

  React.useEffect(() => {
    const currentlyOn = document.body.classList.contains(PRINT_PREVIEW_CLASS);
    setActive(currentlyOn);
    return () => {
      document.body.classList.remove(PRINT_PREVIEW_CLASS);
    };
  }, []);

  const toggle = () => {
    const next = !active;
    document.body.classList.toggle(PRINT_PREVIEW_CLASS, next);
    setActive(next);
  };

  const button = (
    <button
      type="button"
      className="btn ghost print-preview-toggle no-print"
      onClick={toggle}
      aria-pressed={active}
      aria-label={
        active ? "Exit print layout preview" : "Show print layout preview"
      }
      title={active ? "Exit print layout preview" : "Show print layout preview"}
    >
      <Icons.Eye size={14} stroke={2} />
      {active ? "Exit preview" : "Print preview"}
    </button>
  );

  return active ? createPortal(button, document.body) : button;
}

// ---------------- Sidebar ----------------
interface NavItem {
  id?: string;
  kind?: "group";
  href?: string;
  label: string;
  icon?: React.FC<IconFnProps>;
}

export const NAV: NavItem[] = [
  { href: "/start", label: "Start here", icon: Icons.Star },

  { id: "group-overview", kind: "group", label: "Overview" },
  { href: "/", label: "Executive Summary", icon: Icons.Summary },
  { href: "/operator", label: "Operator Economics", icon: Icons.Operator },
  { href: "/developer", label: "Developer Economics", icon: Icons.Developer },
  { href: "/deal", label: "Deal workspace", icon: Icons.Report },

  { id: "group-analysis", kind: "group", label: "Analysis" },
  { href: "/sensitivities", label: "Sensitivities", icon: Icons.Sens },
  { href: "/benchmarks", label: "Benchmarks", icon: Icons.Bench },
  { href: "/compare", label: "Compare", icon: Icons.Bench },
  { href: "/compliance", label: "Compliance", icon: Icons.Check },
  { href: "/rent-economics", label: "Rent Economics", icon: Icons.Sliders },
  { href: "/quotes", label: "Builder quotes", icon: Icons.Quotes },
  { href: "/risk", label: "Risk Analysis", icon: Icons.Sens },
  { href: "/versions", label: "Versions", icon: Icons.Report },

  { id: "group-config", kind: "group", label: "Configuration" },
  { href: "/assumptions", label: "Assumptions", icon: Icons.Inputs },
  { href: "/buildings", label: "Buildings", icon: Icons.Buildings },
  { href: "/checks", label: "Model Checks", icon: Icons.Check },
  { href: "/sites", label: "Sites", icon: Icons.Globe },
  { href: "/report", label: "Report & Export", icon: Icons.Report },
];

export const ROUTE_TITLES: Record<string, string> = {
  "/start": "Start here",
  "/": "Executive Summary",
  "/assumptions": "Assumptions",
  "/buildings": "Buildings",
  "/operator": "Operator Economics",
  "/developer": "Developer Economics",
  "/sensitivities": "Sensitivities",
  "/benchmarks": "Benchmarks",
  "/checks": "Model Checks",
  "/compliance": "Compliance",
  "/risk": "Risk Analysis",
  "/rent-economics": "Rent economics — builder bid → lease cost",
  "/quotes": "Builder quotes — LFL analysis & bid dispersion",
  "/compare": "Compare Scenarios",
  "/versions": "Versions",
  "/sites": "Sites",
  "/report": "Report & Export",
};

function selectedHorizonLabel(scenario: Scenario): string {
  const exitYear =
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears;
  return exitYear < scenario.assumptions.modelYears ? `Y${exitYear}` : "PC";
}

function primaryHurdleStatusLabel(
  status: ModelResult["kpis"]["developerPrimaryHurdleStatus"],
): string {
  if (status === "met") return "hurdle met";
  if (status === "marginal") return "at hurdle";
  return "below hurdle";
}

function dealZoneLabel(
  verdict: ReturnType<typeof getOperatorDealRecommendation>["verdict"],
): "INSIDE" | "CAUTION" | "REVIEW" {
  if (verdict === "WITHIN MODELLED GUARDRAILS") return "INSIDE";
  if (verdict === "REVIEW REQUIRED") return "CAUTION";
  return "REVIEW";
}

function dealZoneClass(
  tone: ReturnType<typeof getOperatorDealRecommendation>["tone"],
): "success" | "warn" | "danger" {
  if (tone === "good") return "success";
  if (tone === "warn") return "warn";
  return "danger";
}

function rentBurdenLabel(rentPctRevenueYear1: number): string {
  return Number.isFinite(rentPctRevenueYear1)
    ? `${(rentPctRevenueYear1 * 100).toFixed(0)}% of Y1 revenue`
    : "unavailable";
}

export function buildSidebarDeveloperReturnIndicator(
  scenario: Scenario,
  result: ModelResult,
) {
  if (scenario.calculationMethod === "dated-v2") {
    return {
      status: "na" as const,
      text: "Dated v2 ledger active — returns shown in Deal workspace",
    };
  }
  const status = result.kpis.developerPrimaryHurdleStatus;
  return {
    status,
    text: `Matched-horizon ungeared return (${selectedHorizonLabel(scenario)}): ${primaryHurdleStatusLabel(status)}`,
  };
}

export function buildHeaderDealZoneView(
  scenario: Scenario,
  result: ModelResult,
) {
  if (scenario.calculationMethod === "dated-v2") {
    return {
      label: "DATED V2",
      cls: "info",
      tooltip:
        "Dated cash-ledger scenario. Legacy operator and developer badges are unavailable.",
    };
  }
  const recommendation = getOperatorDealRecommendation(scenario, result);
  const status = result.kpis.developerPrimaryHurdleStatus;
  return {
    label: dealZoneLabel(recommendation.verdict),
    cls: dealZoneClass(recommendation.tone),
    tooltip: `Operator assessment: ${recommendation.verdict} · rent ${rentBurdenLabel(result.kpis.rentPctRevenueYear1)} · matched-horizon ungeared return (${selectedHorizonLabel(scenario)}): ${primaryHurdleStatusLabel(status)}`,
  };
}

export function Sidebar({
  route,
  navigate,
}: Readonly<{ route: string; navigate: (href: string) => void }>) {
  const { active, result } = useScenario();
  const datedScenario = active.calculationMethod === "dated-v2";
  const flags = datedScenario ? [] : result.flags || [];
  const developerReturn = buildSidebarDeveloperReturnIndicator(active, result);
  const hStat = developerReturn.status;

  let hColor: string;
  if (hStat === "na") {
    hColor = "hsl(var(--c-info))";
  } else if (hStat === "met") {
    hColor = "hsl(var(--c-success))";
  } else if (hStat === "marginal") {
    hColor = "hsl(var(--c-warning))";
  } else {
    hColor = "hsl(var(--c-destructive))";
  }

  const hurdleText = developerReturn.text;

  const checks = useMemo(
    () => (datedScenario ? [] : Model.modelChecks(active, result)),
    [active, datedScenario, result],
  );
  const checksPass = checks.filter((c) => c.level === "pass").length;
  const checksFail = checks.filter((c) => c.level === "fail").length;
  const checksWarn = checks.filter((c) => c.level === "warn").length;
  const checksColor =
    checksFail > 0
      ? "hsl(var(--c-destructive))"
      : checksWarn > 0
        ? "hsl(var(--c-warning))"
        : "hsl(var(--c-success))";

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <div className="brand-mark">
          <Wordmark size="sm" />
        </div>
        <div className="brand-meta">
          <div className="brand-sub">
            {active.assumptions.campusName} · {active.assumptions.operatorName}
          </div>
        </div>
      </div>
      <nav id="primary-nav" className="nav" aria-label="Primary navigation">
        {NAV.map((n, i) => {
          if (n.kind === "group")
            return (
              <div
                key={n.id}
                className="nav-group"
                style={{ marginTop: i === 0 ? 0 : undefined }}
              >
                <div className="nav-label">{n.label}</div>
              </div>
            );
          const isActive = route === n.href;
          const I = n.icon;
          const isChecks = n.href === "/checks";
          const checksBadgeColor = isChecks
            ? checksFail > 0
              ? "hsl(var(--c-destructive))"
              : checksWarn > 0
                ? "hsl(var(--c-warning))"
                : null
            : null;
          return (
            <button
              type="button"
              key={n.href}
              className={`nav-item ${isActive ? "active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              style={{ position: "relative" }}
              onClick={() => {
                if (n.href) navigate(n.href);
                const cb = document.getElementById(
                  "mobile-nav-toggle",
                ) as HTMLInputElement | null;
                if (cb) cb.checked = false;
              }}
            >
              {I && <I size={16} />}
              <span>{n.label}</span>
              {checksBadgeColor && (
                <span
                  style={{
                    position: "absolute",
                    top: 6,
                    right: 8,
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: checksBadgeColor,
                    boxShadow: `0 0 5px ${checksBadgeColor}`,
                  }}
                  aria-label={
                    checksFail > 0
                      ? "Model checks failing"
                      : "Model check warnings"
                  }
                />
              )}
            </button>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        <div
          style={{
            marginBottom: 6,
            fontWeight: 500,
            color: "hsl(var(--c-foreground))",
          }}
        >
          Status
        </div>
        <div className="status-row">
          <span
            aria-hidden="true"
            className="status-dot"
            style={{ background: hColor, boxShadow: `0 0 8px ${hColor}` }}
          ></span>
          <div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
            <Tooltip tip={hurdleText} display="block">
              <span
                style={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hurdleText}
              </span>
            </Tooltip>
          </div>
        </div>
        {datedScenario ? (
          <div style={{ marginTop: 4 }}>
            Dated ledger diagnostics in Deal workspace
          </div>
        ) : (
          <>
            <div style={{ marginTop: 4 }}>
              {flags.length} model flag{flags.length === 1 ? "" : "s"}
            </div>
            <div
              style={{
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                aria-hidden="true"
                className="status-dot"
                style={{
                  background: checksColor,
                  boxShadow: `0 0 6px ${checksColor}`,
                  flexShrink: 0,
                }}
              ></span>
              <Tooltip
                tip={`${checksPass}/${checks.length} model checks pass · ${checksFail} fail · ${checksWarn} warn`}
              >
                <span>
                  {checksPass}/{checks.length} checks pass
                </span>
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

// ---------------- Header ----------------
export function Header({
  route,
}: Readonly<{ route: string; navigate?: (href: string) => void }>) {
  const {
    scenarios,
    activeId,
    setActiveId,
    active,
    result,
    resetActiveToDefault,
    canUndo,
    canRedo,
    undo,
    redo,
    mcResult,
    results,
    sites,
    activeSiteId,
    setActiveSiteId,
  } = useScenario();
  const auth = useAuth();
  const [printBusy, setPrintBusy] = useState(false);
  type Theme = "dark" | "light" | "auto";
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem("cls-theme");
      if (stored === "dark" || stored === "light" || stored === "auto")
        return stored;
    } catch {
      /* noop */
    }
    return "dark";
  });
  useEffect(() => {
    const root = document.documentElement;
    const applyResolved = (mode: "light" | "dark") => {
      if (mode === "light") {
        root.classList.add("light");
        root.classList.remove("dark");
      } else {
        root.classList.add("dark");
        root.classList.remove("light");
      }
    };

    try {
      localStorage.setItem("cls-theme", theme);
    } catch {
      /* noop */
    }

    if (theme === "auto") {
      const mq = globalThis.matchMedia("(prefers-color-scheme: dark)");
      const resolve = () => (mq.matches ? "dark" : "light");
      applyResolved(resolve());
      const handler = () => applyResolved(resolve());
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }

    applyResolved(theme);
  }, [theme]);
  const applyTheme = (mode: Theme) => setTheme(mode);

  const zopaVerdict = buildHeaderDealZoneView(active, result);

  // Delight: Scenario chip pop animation on scenario switch
  const prevActiveIdRef = useRef(activeId);
  const [chipPop, setChipPop] = useState(false);
  useEffect(() => {
    if (prevActiveIdRef.current !== activeId) {
      prevActiveIdRef.current = activeId;
      setChipPop(true);
      const t = setTimeout(() => setChipPop(false), 500);
      return () => clearTimeout(t);
    }
  }, [activeId]);

  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    const cb = document.getElementById(
      "mobile-nav-toggle",
    ) as HTMLInputElement | null;
    if (!cb) return;
    const onChange = () => setNavOpen(cb.checked);
    cb.addEventListener("change", onChange);
    setNavOpen(cb.checked);
    return () => cb.removeEventListener("change", onChange);
  }, []);
  const toggleNav = () => {
    const cb = document.getElementById(
      "mobile-nav-toggle",
    ) as HTMLInputElement | null;
    if (!cb) return;
    cb.checked = !cb.checked;
    cb.dispatchEvent(new Event("change", { bubbles: true }));
  };

  // Workspace tools popup: Escape closes and returns focus to the summary.
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && el.open) {
        e.preventDefault();
        e.stopPropagation();
        el.open = false;
        el.querySelector("summary")?.focus();
      }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="header no-print">
      <button
        type="button"
        className="hamburger-btn"
        data-testid="mobile-nav-toggle"
        aria-label="Toggle navigation"
        aria-expanded={navOpen}
        aria-controls="primary-nav"
        onClick={toggleNav}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <div>
        <div className="header-eyebrow">{active.assumptions.address}</div>
        <h1>{ROUTE_TITLES[route] || "TENURE"}</h1>
      </div>
      <div className="header-actions">
        <div className="header-context">
          <span className="scenario-chip">
            <span className="dot" aria-hidden="true"></span>
            <span style={{ color: "hsl(var(--c-foreground-muted))" }}>
              Site
            </span>
            <select
              id="site-select"
              name="site"
              data-testid="site-select"
              className="select"
              value={activeSiteId ?? ""}
              onChange={(e) => setActiveSiteId(e.target.value || null)}
              style={{
                border: "none",
                background: "transparent",
                padding: "4px 22px 4px 4px",
              }}
              aria-label="Select site"
              title="Active site filter"
            >
              <option value="">All sites</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </span>
          <span
            className={`scenario-chip${chipPop ? " scenario-chip-pop" : ""}`}
          >
            <span className="dot" aria-hidden="true"></span>
            <span style={{ color: "hsl(var(--c-foreground-muted))" }}>
              Scenario
            </span>
            <select
              id="scenario-select"
              name="scenario"
              data-testid="scenario-select"
              className="select"
              value={activeId}
              onChange={(e) => setActiveId(e.target.value)}
              style={{
                border: "none",
                background: "transparent",
                padding: "4px 22px 4px 4px",
              }}
              aria-label="Select scenario"
              title="Active scenario (press 1–4 to switch)"
            >
              {activeSiteId === null
                ? groupScenariosBySite(scenarios, sites).map((group) => (
                    <optgroup
                      key={group.siteId ?? "ungrouped"}
                      label={group.siteName}
                    >
                      {group.scenarios.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </optgroup>
                  ))
                : filterScenariosBySite(scenarios, activeSiteId).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
            </select>
          </span>
        </div>
        <Tooltip tip={zopaVerdict.tooltip}>
          <span
            className={`pill ${zopaVerdict.cls}`}
            style={{ fontWeight: 700, fontSize: 10, letterSpacing: "0.06em" }}
            tabIndex={0}
            aria-label={`Deal zone: ${zopaVerdict.label}`}
          >
            {zopaVerdict.label}
          </span>
        </Tooltip>
        <Tooltip tip="Model is live — recomputes automatically as you edit">
          <span
            className="p-micro"
            style={{
              color: "hsl(var(--c-success))",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            tabIndex={0}
            aria-label="Live status"
          >
            <span
              className="status-dot status-dot-live"
              style={{ background: "hsl(var(--c-success))" }}
              aria-hidden="true"
            ></span>
            Live
          </span>
        </Tooltip>
        <SyncStatus
          state={deriveSyncState({
            passcodeMode: auth.usePasscodeFallback,
            pendingCount: syncQueue.getPendingCount(),
          })}
        />
        <Tooltip tip="Print / export to PDF">
          <button
            type="button"
            className="btn ghost"
            data-testid="print-pdf-btn"
            aria-label="Print / export to PDF"
            disabled={printBusy}
            onClick={async () => {
              setPrintBusy(true);
              try {
                if (active.calculationMethod === "dated-v2") {
                  const { buildDealReport, exportDealPdf } =
                    await import("./deal-report");
                  await exportDealPdf(
                    buildDealReport(scenarios, { baselineId: active.id }),
                  );
                } else {
                  const { printPdf } = await import("./export-pdf");
                  await printPdf(
                    active,
                    result,
                    scenarios,
                    results,
                    mcResult || undefined,
                  );
                }
              } catch (e) {
                console.error("Print failed:", e);
                toastGlobal(
                  "Print failed. Falling back to browser print.",
                  "error",
                  4000,
                );
                globalThis.print();
              } finally {
                setPrintBusy(false);
              }
            }}
            style={{ opacity: printBusy ? 0.5 : undefined }}
          >
            <Icons.Print size={14} /> {printBusy ? "Printing…" : "Print"}
          </button>
        </Tooltip>
        <PrintPreviewToggle />
        <details
          className="header-tools"
          data-testid="workspace-tools"
          ref={detailsRef}
        >
          <summary>Workspace tools</summary>
          <div className="header-tools-popup">
            <fieldset
              className="theme-toggle theme-toggle-menu"
              title="Theme"
              aria-label="Theme"
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              <legend style={{ display: "none" }}>Theme</legend>
              <button
                type="button"
                className={theme === "light" ? "active" : ""}
                onClick={() => applyTheme("light")}
                aria-label="Light theme"
                aria-pressed={theme === "light"}
              >
                ☀ Light
              </button>
              <button
                type="button"
                className={theme === "dark" ? "active" : ""}
                onClick={() => applyTheme("dark")}
                aria-label="Dark theme"
                aria-pressed={theme === "dark"}
              >
                🌙 Dark
              </button>
              <button
                type="button"
                className={theme === "auto" ? "active" : ""}
                onClick={() => applyTheme("auto")}
                aria-label="Auto theme — follows your device's light/dark setting"
                title="Follows your device's light/dark setting"
                aria-pressed={theme === "auto"}
              >
                Auto
              </button>
            </fieldset>
            <button
              type="button"
              className="btn ghost"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
              style={{ opacity: canUndo ? 1 : 0.4 }}
            >
              <Icons.Undo size={14} /> Undo
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z / Ctrl+Y)"
              style={{ opacity: canRedo ? 1 : 0.4 }}
            >
              <Icons.Redo size={14} /> Redo
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={resetActiveToDefault}
              title="Reset active scenario to defaults"
            >
              <Icons.Reset size={14} /> Reset
            </button>
            <ImportExportUI />
            {auth.authed && !auth.usePasscodeFallback && auth.email && (
              <div
                className="row gap-1"
                style={{ alignItems: "center", marginLeft: 4 }}
              >
                <Tooltip tip={auth.email}>
                  <span
                    className="p-micro"
                    style={{
                      color: "hsl(var(--c-foreground-muted))",
                      maxWidth: 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    tabIndex={0}
                    aria-label={`Signed in as ${auth.email}`}
                  >
                    {auth.email}
                  </span>
                </Tooltip>
                <Tooltip tip="Sign out">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      auth.logout();
                      toastGlobal("Signed out", "success", 2000);
                    }}
                    style={{ padding: "4px 8px" }}
                    aria-label="Sign out"
                  >
                    <Icons.Logout size={14} />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        </details>
      </div>
    </header>
  );
}

// ---------------- Tooltip ----------------
/** Minimal hover/focus tooltip primitive for non-chart UI elements.
 *  Does not replace chart tooltips in charts.tsx. */
export function Tooltip({
  children,
  tip,
  display = "inline-block",
}: Readonly<{
  children: React.ReactElement;
  tip: React.ReactNode;
  display?: "inline-block" | "block";
}>) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({ left: -9999, top: -9999 });
  const id = React.useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const margin = 6;
    const roomAbove = triggerRect.top;
    const roomBelow = window.innerHeight - triggerRect.bottom;
    const placeTop =
      roomAbove >= tooltipRect.height + margin ||
      roomBelow < tooltipRect.height + margin;
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const halfW = tooltipRect.width / 2;
    const minLeft = 8;
    const maxLeft = Math.max(
      minLeft,
      window.innerWidth - tooltipRect.width - 8,
    );
    setPos({
      left: Math.max(minLeft, Math.min(maxLeft, triggerCenter - halfW)),
      top: placeTop ? undefined : triggerRect.bottom + margin,
      bottom: placeTop
        ? window.innerHeight - triggerRect.top + margin
        : undefined,
    });
  }, [open]);

  if (!tip) return children;

  const child = children;
  return (
    <span ref={triggerRef} style={{ position: "relative", display }}>
      {React.cloneElement(child, {
        onMouseEnter: (e: React.MouseEvent) => {
          setOpen(true);
          child.props.onMouseEnter?.(e);
        },
        onMouseLeave: (e: React.MouseEvent) => {
          setOpen(false);
          child.props.onMouseLeave?.(e);
        },
        onFocus: (e: React.FocusEvent) => {
          setOpen(true);
          child.props.onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent) => {
          setOpen(false);
          child.props.onBlur?.(e);
        },
        ...(open ? { "aria-describedby": id } : {}),
      })}
      {open &&
        createPortal(
          <span
            ref={tooltipRef}
            id={id}
            role="tooltip"
            style={{
              position: "fixed",
              ...pos,
              maxWidth: 280,
              padding: "6px 10px",
              borderRadius: 6,
              background: "hsl(var(--c-card-elevated))",
              color: "hsl(var(--c-foreground))",
              border: "1px solid hsl(var(--c-border))",
              boxShadow: "var(--shadow-lg)",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.4,
              whiteSpace: "normal",
              wordBreak: "break-word",
              zIndex: 100,
              pointerEvents: "none",
            }}
          >
            {tip}
          </span>,
          document.body,
        )}
    </span>
  );
}

// ---------------- KPICard ----------------
const handleKpiKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
};

const KPI_STATUS_MAP: Record<string, { cls: string; label: string }> = {
  good: { cls: "success", label: "PASS" },
  na: { cls: "success", label: "N/A" },
  bad: { cls: "danger", label: "FAIL" },
  warn: { cls: "warn", label: "WATCH" },
};

function kpiAccentColor(status: KPICardProps["status"]): string {
  if (status === "good" || status === "na") return "hsl(var(--c-success))";
  if (status === "bad") return "hsl(var(--c-destructive))";
  if (status === "warn") return "hsl(var(--c-warning))";
  return "hsl(var(--c-primary))";
}

export function KPICard({
  eyebrow,
  value,
  hint,
  foot,
  tooltip,
  accent = "primary",
  size = "lg",
  spark,
  status,
  className,
}: Readonly<KPICardProps>) {
  const accentColor = kpiAccentColor(status);
  const pill = status
    ? (KPI_STATUS_MAP[status] ?? { cls: "info", label: "INFO" })
    : null;

  let statFontSize: number;
  if (size === "xl") {
    statFontSize = 44;
  } else if (size === "lg") {
    statFontSize = 34;
  } else {
    statFontSize = 24;
  }

  const { ref, isInView } = useInView({ triggerOnce: true });
  const valueStr = typeof value === "string" ? value : String(value ?? "");
  const parsed = parseNumericValue(valueStr);
  const animated = useCountUp(isInView && parsed ? parsed.raw : 0, 800, {
    decimals: parsed?.decimals ?? 0,
  });
  const display = parsed && isInView ? formatParsed(parsed, animated) : value;

  return (
    <div
      ref={ref}
      className={`card ${accent === "glow" ? "glow" : "elevated"} card-pad ${className || ""}`}
    >
      <div className="row between" style={{ alignItems: "baseline" }}>
        <span className="eyebrow">
          {eyebrow}
          {tooltip ? (
            <Tooltip tip={tooltip}>
              <span
                style={{
                  marginLeft: 4,
                  opacity: 0.5,
                  fontSize: 11,
                  cursor: "help",
                }}
                tabIndex={0}
                aria-label={tooltip}
              >
                ⓘ
              </span>
            </Tooltip>
          ) : null}
        </span>
        {pill ? <span className={`pill ${pill.cls}`}>{pill.label}</span> : null}
      </div>
      <div
        className="stat-display stat-display-copyable"
        style={{
          fontSize: statFontSize,
          marginTop: 6,
          color: accentColor,
          transition: "color 0.3s ease",
        }}
        title="Click to copy"
        role="button"
        tabIndex={0}
        aria-label={`Copy ${typeof display === "string" ? display : valueStr}`}
        onClick={() => {
          const text = typeof display === "string" ? display : valueStr;
          navigator.clipboard?.writeText(text).then(
            () => toastGlobal(`Copied: ${text}`, "success", 2000),
            () => {
              /* clipboard unavailable */
            },
          );
        }}
        onKeyDown={handleKpiKeyDown}
      >
        {display}
      </div>
      {hint ? (
        <div className="p-small" style={{ marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
      {spark}
      {foot ? (
        <div className="p-micro" style={{ marginTop: 8 }}>
          {foot}
        </div>
      ) : null}
    </div>
  );
}

// ---------------- Section ----------------
export function Section({
  eyebrow,
  title,
  lede,
  right,
  children,
  className,
}: Readonly<SectionProps>) {
  return (
    <section
      className={`fade-in ${className || ""}`}
      style={{ marginTop: "var(--space-8)" }}
    >
      <div className="page-strip">
        <div>
          {eyebrow ? (
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {eyebrow}
            </div>
          ) : null}
          {title ? (
            <h2 className="h4" style={{ margin: 0 }}>
              {title}
            </h2>
          ) : null}
          {lede ? (
            <div className="lede" style={{ marginTop: 6 }}>
              {lede}
            </div>
          ) : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </section>
  );
}

// ---------------- Flag ----------------
export function FlagItem({ level, message }: Readonly<FlagItemProps>) {
  let cls: string;
  if (level === "error") {
    cls = "danger";
  } else if (level === "warning") {
    cls = "warn";
  } else {
    cls = "info";
  }
  return (
    <div className="row gap-2 well">
      <span className={`pill ${cls}`}>{level.toUpperCase()}</span>
      <span style={{ fontSize: 13, color: "hsl(var(--c-foreground))" }}>
        {message}
      </span>
    </div>
  );
}

// ---------------- Field / NumberInput ----------------
interface FieldProps {
  label: string;
  hint?: string;
  children?: React.ReactNode;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
}

export function Field({
  label,
  hint,
  children,
  suffix,
  prefix,
}: Readonly<FieldProps>) {
  return (
    <label className="col" style={{ gap: 6 }}>
      <span className="label">{label}</span>
      <div
        className="row gap-2"
        style={{ alignItems: "center", flexWrap: "wrap" }}
      >
        {prefix ? (
          <span
            style={{
              color: "hsl(var(--c-foreground-muted))",
              fontSize: 13,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {prefix}
          </span>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {suffix ? (
          <span
            style={{
              color: "hsl(var(--c-foreground-muted))",
              fontSize: 13,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {hint ? <span className="p-micro">{hint}</span> : null}
    </label>
  );
}

function isValidNumberText(text: string): boolean {
  const cleaned = String(text).replace(/[, $%]/g, "");
  return Number.isFinite(Number(cleaned));
}

interface NumberInputProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  format?: (v: number) => string;
  error?: string;
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  format,
  error,
}: Readonly<NumberInputProps>) {
  const [text, setText] = useState(format ? format(value) : String(value));
  useEffect(() => {
    setText(format ? format(value) : String(value));
  }, [value, format]);
  const commit = (v: string) => {
    const cleaned = String(v).replace(/[, $%]/g, "");
    const n = Number(cleaned);
    if (Number.isFinite(n)) {
      let next = n;
      if (min != null) next = Math.max(min, next);
      if (max != null) next = Math.min(max, next);
      onChange(next);
    } else {
      setText(format ? format(value) : String(value));
    }
  };
  const invalidText = !isValidNumberText(text);
  const showErrorBorder = !!error || invalidText;
  const inputId = useId().replace(/:/g, "-");
  const errorId = `${inputId}-error`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <input
        id={inputId}
        className="input"
        type="text"
        inputMode="decimal"
        aria-invalid={showErrorBorder}
        aria-describedby={error ? errorId : undefined}
        value={text}
        style={
          showErrorBorder
            ? { borderColor: "hsl(var(--c-destructive))" }
            : undefined
        }
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      {error ? (
        <span
          id={errorId}
          style={{ fontSize: 12, color: "hsl(var(--c-destructive))" }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}

interface PercentInputProps {
  value: number;
  onChange: (v: number) => void;
  digits?: number;
  error?: string;
}

export function PercentInput({
  value,
  onChange,
  digits = 1,
  error,
}: Readonly<PercentInputProps>) {
  const n = value === 0 ? 0 : value;
  return (
    <NumberInput
      value={Number((n * 100).toFixed(digits + 2))}
      onChange={(v) => onChange(v / 100)}
      format={(v) => `${Number(v === 0 ? 0 : v).toFixed(digits)}%`}
      error={error}
    />
  );
}

interface CurrencyInputProps {
  value: number;
  onChange: (v: number) => void;
  error?: string;
}

export function CurrencyInput({
  value,
  onChange,
  error,
}: Readonly<CurrencyInputProps>) {
  return (
    <NumberInput
      value={value}
      min={0}
      onChange={onChange}
      format={(v) => `$${Math.round(Number(v)).toLocaleString("en-AU")}`}
      error={error}
    />
  );
}

// ---------------- KV row used inside cards ----------------
export function KV({
  k,
  v,
  accent,
}: Readonly<{ k: React.ReactNode; v: React.ReactNode; accent?: string }>) {
  return (
    <div
      className="row between"
      style={{
        padding: "6px 0",
        borderBottom: "1px dashed hsl(var(--c-border-subtle))",
      }}
    >
      <span className="p-small">{k}</span>
      <span
        className="mono"
        style={{ color: accent || "hsl(var(--c-foreground))", fontWeight: 500 }}
      >
        {v}
      </span>
    </div>
  );
}

// ---------------- ChartDownloadButton ----------------
export function ChartDownloadButton({
  selector,
  filename = "chart.png",
  title = "Download chart as PNG",
}: Readonly<{ selector: string; filename?: string; title?: string }>) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      const url = await captureSvgSelector(selector);
      if (!url) return;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Tooltip tip={title}>
      <button
        type="button"
        className="btn ghost"
        onClick={handleClick}
        disabled={busy}
        style={{ padding: "4px 6px" }}
        aria-label={title}
      >
        <Icons.Download size={14} />
      </button>
    </Tooltip>
  );
}

// ---------------- KeyboardHelp ----------------
export function KeyboardHelp({
  open,
  onClose,
}: Readonly<{ open: boolean; onClose: () => void }>) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);
  const trapRef = useFocusTrap(open);
  useEffect(() => {
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        e.preventDefault();
      }
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const shortcuts = [
    { keys: "1 – 4", action: "Switch scenario" },
    { keys: "P", action: "Print" },
    { keys: "E", action: "Executive summary" },
    { keys: "R", action: "Report" },
    { keys: "→ or L", action: "Next tab" },
    { keys: "← or H", action: "Previous tab" },
    { keys: "Ctrl + Z", action: "Undo" },
    { keys: "Ctrl + Shift + Z or Ctrl + Y", action: "Redo" },
    { keys: "?", action: "Toggle this help" },
  ];

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "hsl(var(--c-background) / 0.7)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        className="card elevated"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 90vw)",
          maxHeight: "80vh",
          overflow: "auto",
          padding: "24px 28px",
        }}
      >
        <div
          className="row between"
          style={{ alignItems: "center", marginBottom: 16 }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            Keyboard shortcuts
          </h3>
          <button
            type="button"
            className="btn ghost"
            onClick={onClose}
            style={{ padding: "4px 8px", fontSize: 16 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="col" style={{ gap: 8 }}>
          {shortcuts.map((s) => (
            <div
              key={s.action}
              className="row between"
              style={{
                padding: "6px 0",
                borderBottom: "1px dashed hsl(var(--c-border-subtle))",
              }}
            >
              <span
                className="mono"
                style={{ fontSize: 13, color: "hsl(var(--c-foreground))" }}
              >
                {s.keys}
              </span>
              <span
                className="p-small"
                style={{ color: "hsl(var(--c-foreground-muted))" }}
              >
                {s.action}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
